<template>
  <div :class="{'container': !isMobile && withContainer}" data-testid="glide-slider-container">
    <div class="slide-container">
      <div
        @click="handlePrev"
        class="prev-button"
        :class="{'disabled-button': isFirstSlide}"
        v-if="!isFirstSlide && !isMobile && showArrows"
        data-testid="glide-prev-button"
      >
        <i class="icon-arrow-left xxs"></i>
      </div>
      <div v-else-if="!isMobile" data-testid="placeholder-prev-button" class="button-placeholder placeholder-prev"></div>
      <vue-glide
        :options="options"
        ref="slider"
        v-model="active"
        :per-view="calculateSlidesPerView"
        :breakpoints="breakpoints"
        @glide:resize="handleResize"
        :dragThreshold="handleDragThreshold"

      >
        <vue-glide-slide v-for="(slide, index) in slides" :key="index">
          <div class="items-container">
            <OfferCard :is-compact="isCompact" :content="slide" :with-description="isWithDescription" :isFeaturedDeals="isFeaturedDeals" />
          </div>
        </vue-glide-slide>
      </vue-glide>
      <button
        @click="handleNext"
        class="next-button"
        :class="{'disabled-button': isLastSlide}"
        v-if="!isLastSlide && !isMobile && showArrows"
        data-testid="glide-next-button"
      >
        <i class="icon-arrow-right xxs"></i>
      </button>
      <div v-else-if="!isMobile" data-testid="placeholder-next-button" class="button-placeholder placeholder-next"></div>
    </div>
  </div>
</template>

<script>
import { Glide, GlideSlide } from 'vue-glide-js'
import OfferCard from '@/components/offers/OfferCard'
import { media } from '@/infrastructure/Utils'
export default {
  name: 'GlideSlider',
  components: {
    OfferCard,
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide
  },
  props: {
    slidesPerView: {
      type: Number,
      default: 4
    },
    slides: {
      type: Array,
      required: true
    },
    isFeaturedDeals: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    withContainer: {
      type: Boolean,
      default: true
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateInnerWidth)
    this.updateInnerWidth()
  },
  unmounted() {
    window.removeEventListener('resize', this.updateInnerWidth)
  },
  data() {
    return {
      options: {
        type: 'slider',
        startAt: 1,
        rewind: false,
        peek: {
          before: 0,
          after: 0
        },
        bound: true
      },
      active: 0,
      currentInnerWidth: 0
    }
  },
  computed: {
    breakpoints() {
      if (this.isFeaturedDeals) {
        return {
          768: {
            perView: 1
          }
        }
      }
      return {
        768: {
          perView: 2
        }
      }
    },
    isValidSlide() {
      return this.active + this.calculateSlidesPerView + 1 <= this.slides.length - 1 - this.calculateSlidesPerView
    },
    handleDragThreshold() {
      if (this.slides.length < this.slidesPerView && !this.isMobile) {
        return false
      }
      if (this.slides.length < this.breakpoints['768'].perView && this.isMobile) {
        return false
      }
      return 120
    },
    showArrows() {
      return this.slides.length > this.slidesPerView
    },
    isFirstSlide() {
      return this.active === 0
    },
    isLastSlide() {
      return this.active === this.slides.length - this.calculateSlidesPerView
    },
    calculateSpaceBetween() {
      if (this.isMobile) {
        return 15
      }
      return 30
    },
    isCompact() {
      return this.isTablet || this.isFeaturedDeals
    },
    isWithDescription() {
      return !this.isTablet || (!this.isMobile && this.isFeaturedDeals)
    },
    isTablet() {
      return this.currentInnerWidth < media('tablet-l')
    },
    calculateSlidesPerView() {
      if (this.isFeaturedDeals) {
        if (this.isMobile) {
          return 1
        }
        if (this.slides.length <= this.slidesPerView) {
          return this.slides.length
        }
      }
      if (this.isMobile) {
        return 2
      }
      return this.slidesPerView
    }
  },
  methods: {
    calculatePeek(value) {
      if (this.isMobile) {
        if (value === 0) {
          this.$refs.slider.glide.update({ peek: { before: 15, after: 85 } })
        } else if (value === this.slides.length - this.calculateSlidesPerView) {
          this.$refs.slider.glide.update({ peek: { before: 85, after: 15 } })
        } else {
          this.$refs.slider.glide.update({ peek: { before: 50, after: 50 } })
        }
      }
    },
    updateInnerWidth() {
      this.currentInnerWidth = window.innerWidth
      this.calculatePeek(this.active)
    },
    handleResize() {
      this.$refs.slider.glide.update({ gap: this.calculateSpaceBetween })
    },
    handlePrev() {
      const slide = this.active - this.calculateSlidesPerView > 0
        ? this.active - this.calculateSlidesPerView
        : 0
      this.$refs.slider.glide.go(`=${slide}`)
    },
    handleNext() {
      if (this.isValidSlide) {
        const slide = this.active + this.calculateSlidesPerView
        this.$refs.slider.glide.go(`=${slide}`)
      } else {
        this.$refs.slider.glide.go(`=${this.slides.length - this.calculateSlidesPerView}`)
      }
    }
  },
  watch: {
    active(newVal) {
      this.calculatePeek(newVal)
    }
  }
}
</script>

<style scoped lang="scss">
.items-container {
  padding: 0 0 2rem 0;
}
.overflow-hidden {
  overflow: hidden;
}
.slide-container {

  ul,
  ul li {
    margin:0;
    padding: 0;
    text-indent: 0;
    list-style-type: none;
  }

  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  width: 100%;
  .prev-button, .next-button {
    display:flex;
    justify-content: center;
    align-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    border: 1.5px solid color('black');
    background-color: #ffffff;
    cursor: pointer;
    position: absolute;
    z-index: zindex("z-10");
  }
  .prev-button{
    left: -5rem;
  }

  .next-button{
    right: -5rem;
  }
  .button-placeholder{
    width: 3rem;
    height: 3rem;
    position: absolute;
  }
  .placeholder-next{
    right: -5rem;
  }
  .placeholder-prev{
    left: -5rem;
  }
}
</style>
